import { JSONSchema7, JSONSchema7Definition } from 'json-schema';

type AnySchema = Record<string, any>;

const isObject = (value: unknown): value is AnySchema =>
    !!value && typeof value === 'object' && !Array.isArray(value);

const resolveLocalRef = (ref: string, rootSchema: AnySchema): AnySchema | undefined => {
    if (!ref.startsWith('#')) {
        return undefined;
    }
    const segments = ref
        .slice(1)
        .split('/')
        .filter(Boolean)
        .map(segment => segment.replace(/~1/g, '/').replace(/~0/g, '~'));
    let node: any = rootSchema;
    for (const segment of segments) {
        if (!isObject(node) || !(segment in node)) {
            return undefined;
        }
        node = node[segment];
    }
    return isObject(node) ? node : undefined;
};

const resolveSchemaNode = (schema: AnySchema, rootSchema: AnySchema, seen: Set<AnySchema>): AnySchema => {
    if (seen.has(schema)) {
        return schema;
    }
    seen.add(schema);

    let resolved: AnySchema = schema;
    if (typeof schema.$ref === 'string') {
        const target = resolveLocalRef(schema.$ref, rootSchema);
        if (target) {
            const { $ref, ...rest } = schema;
            resolved = { ...resolveSchemaNode(target, rootSchema, seen), ...rest };
        }
    }
    if (Array.isArray(resolved.allOf)) {
        const { allOf, ...rest } = resolved;
        resolved = allOf.reduce((acc: AnySchema, part: unknown) => {
            if (!isObject(part)) {
                return acc;
            }
            const resolvedPart = resolveSchemaNode(part, rootSchema, seen);
            return {
                ...acc,
                ...resolvedPart,
                properties: { ...(acc.properties || {}), ...(resolvedPart.properties || {}) },
                required: [...(acc.required || []), ...(resolvedPart.required || [])],
            };
        }, rest);
    }
    return resolved;
};

const isEmptyValue = (value: unknown): boolean => {
    if (value === undefined) {
        return true;
    }
    if (Array.isArray(value)) {
        return value.length === 0;
    }
    if (isObject(value)) {
        return Object.keys(value).length === 0;
    }
    return false;
};

const computeNodeDefaults = (schema: JSONSchema7Definition | undefined, rootSchema: AnySchema): unknown => {
    if (!isObject(schema)) {
        return undefined;
    }
    const node = resolveSchemaNode(schema, rootSchema, new Set());

    if (node.default !== undefined) {
        return Array.isArray(node.default)
            ? node.default.filter((entry: unknown) => entry !== undefined)
            : node.default;
    }
    if (node.const !== undefined) {
        return node.const;
    }

    if (isObject(node.properties)) {
        const result = Object.entries(node.properties).reduce((acc, [key, propSchema]) => {
            const value = computeNodeDefaults(propSchema as JSONSchema7Definition, rootSchema);
            if (!isEmptyValue(value)) {
                acc[key] = value;
            }
            return acc;
        }, {} as Record<string, unknown>);
        return Object.keys(result).length ? result : undefined;
    }

    return undefined;
};

export const computeSchemaDefaults = (schema: JSONSchema7): Record<string, any> => {
    const defaults = computeNodeDefaults(schema, schema as AnySchema);
    return isObject(defaults) ? defaults : {};
};
